import { mapById, replayChannelIds } from './maps';
import { seededRandom, seededShuffle } from './random';
import type {
  CollisionEffect,
  FateMapDefinition,
  PhysicsReplay,
  RaceMarble,
  RaceOptions,
  RacePhase,
  ScheduledRaceEvent,
  WinMode,
} from './types';
import type { MarbleEntry } from './input';

const MARBLE_COLORS = [
  '#ff668b', '#ffd166', '#4fd6c8', '#8d7dff', '#ff9c55', '#54afff',
  '#a9dc5d', '#ed78df', '#ff7c67', '#58d88b', '#b385f2', '#e6ba4b',
] as const;

export function participantColor(index: number): string {
  if (index < MARBLE_COLORS.length) return MARBLE_COLORS[index];
  const hue = Math.round((index * 137.508 + 18) % 360);
  return `hsl(${hue} 68% 62%)`;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

export function targetRankForMode(mode: WinMode, requestedRank: number | undefined, count: number): number {
  if (mode === 'first') return 1;
  if (mode === 'last') return count;
  if (!Number.isInteger(requestedRank) || (requestedRank ?? 0) < 1 || (requestedRank ?? 0) > count) {
    throw new Error(`당첨 순위는 1부터 ${count} 사이의 정수여야 해요.`);
  }
  return requestedRank!;
}

function validateReplay(replay: PhysicsReplay, entryCount: number, options: RaceOptions): void {
  if (replay.bodyCount !== entryCount) throw new Error('마블 수와 물리 리플레이가 일치하지 않습니다.');
  if (replay.seed !== options.seed) throw new Error('추첨 번호와 물리 리플레이가 일치하지 않습니다.');
  if (options.mapSelection !== replay.mapId) throw new Error('선택한 맵과 물리 리플레이가 일치하지 않습니다.');
  if (replay.finishOrder.length !== entryCount || replay.finishTimes.length !== entryCount) {
    throw new Error('완주 기록이 올바르지 않습니다.');
  }
  if (replay.forcedFinishCount !== 0) {
    throw new Error('일부 마블이 물리 트랙을 완주하지 못했습니다. 새 추첨 번호로 다시 시작해 주세요.');
  }
  const expectedLaps = mapById(replay.mapId).field?.circuit?.laps;
  if (expectedLaps !== undefined && (replay.lapCount !== expectedLaps || replay.lapStartTimes?.length !== entryCount * expectedLaps
    || !replay.lapStartTimes.every((time, index, times) => Number.isFinite(time) && time >= 0
      && (index % expectedLaps === 0 ? time === 0 : time > times[index - 1] && time < replay.finishTimes[Math.floor(index / expectedLaps)])))) {
    throw new Error('마블별 랩 기록이 올바르지 않습니다.');
  }
  const expectedValues = replay.frameCount * entryCount * replay.valuesPerBody;
  if (replay.valuesPerBody < 5 || replay.positions.length !== expectedValues) {
    throw new Error('물리 리플레이 프레임이 올바르지 않습니다.');
  }
  const expectedSpinnerIds = replayChannelIds(mapById(replay.mapId));
  if (
    replay.spinnerIds.length !== expectedSpinnerIds.length
    || replay.spinnerIds.some((id, index) => id !== expectedSpinnerIds[index])
    || replay.spinnerAngles.length !== replay.frameCount * replay.spinnerIds.length
    || !replay.spinnerAngles.every(Number.isFinite)
  ) {
    throw new Error('회전 장치 리플레이가 올바르지 않습니다.');
  }
  if (
    !Array.isArray(replay.events)
    || replay.events.some((event) => !Number.isFinite(event.announcesAt)
      || !Number.isFinite(event.startsAt)
      || !Number.isFinite(event.duration)
      || event.duration < 0)
    || (!options.eventsEnabled && replay.events.length > 0)
  ) {
    throw new Error('반전 이벤트 리플레이가 올바르지 않습니다.');
  }
}

export interface RaceEventPresentation {
  readonly event: ScheduledRaceEvent;
  readonly phase: 'announcing' | 'active';
  readonly countdown: number;
}

export class MarbleRaceEngine {
  readonly map: FateMapDefinition;
  readonly targetRank: number;
  readonly marbles: RaceMarble[];
  readonly events: readonly ScheduledRaceEvent[];
  readonly replay: PhysicsReplay;
  readonly seed: string;
  readonly winMode: WinMode;
  elapsed = 0;
  phase: RacePhase = 'ready';
  readonly finishers: RaceMarble[] = [];
  private readonly marbleByBodyId: readonly RaceMarble[];
  private readonly spinnerIndexById: ReadonlyMap<string, number>;

  constructor(entries: readonly MarbleEntry[], options: RaceOptions, replay: PhysicsReplay) {
    if (entries.length < 2) throw new Error('마블은 두 개 이상 필요해요.');
    validateReplay(replay, entries.length, options);
    this.replay = replay;
    this.spinnerIndexById = new Map(replay.spinnerIds.map((id, index) => [id, index]));
    this.map = mapById(replay.mapId);
    this.seed = options.seed;
    this.winMode = options.winMode;
    this.targetRank = targetRankForMode(options.winMode, options.requestedRank, entries.length);

    // 물리 계산이 끝난 뒤 익명 바디에 참가자를 배정해 이름·색상이 운동에 영향을 주지 않게 한다.
    const outcomeOrder = seededShuffle(entries, options.seed, 'outcome-order');
    const rankByBodyId = new Map<number, number>();
    const entryByBodyId = new Map<number, MarbleEntry>();
    replay.finishOrder.forEach((bodyId, index) => {
      rankByBodyId.set(bodyId, index + 1);
      entryByBodyId.set(bodyId, outcomeOrder[index]);
    });
    const originalSerial = new Map(entries.map((entry, index) => [entry.id, index + 1]));
    const colorByParticipant = new Map<string, string>();
    for (const entry of entries) {
      const key = entry.name.toLocaleLowerCase('ko-KR');
      if (!colorByParticipant.has(key)) colorByParticipant.set(key, participantColor(colorByParticipant.size));
    }
    const decorationRandom = seededRandom(options.seed, 'decoration');
    this.marbleByBodyId = Array.from({ length: entries.length }, (_, bodyId) => {
      const entry = entryByBodyId.get(bodyId);
      const finishRank = rankByBodyId.get(bodyId);
      if (!entry || !finishRank) throw new Error('물리 리플레이의 완주 순서가 올바르지 않습니다.');
      const offset = bodyId * replay.valuesPerBody;
      const serial = originalSerial.get(entry.id) ?? bodyId + 1;
      return {
        ...entry,
        serial,
        bodyId,
        finishRank,
        targetFinishTime: replay.finishTimes[bodyId],
        color: colorByParticipant.get(entry.name.toLocaleLowerCase('ko-KR')) ?? participantColor(serial - 1),
        laneOffset: 0,
        motionPhase: decorationRandom() * Math.PI * 2,
        x: replay.positions[offset],
        y: replay.positions[offset + 1],
        vx: replay.positions[offset + 2],
        vy: replay.positions[offset + 3],
        progress: replay.positions[offset + 4],
        lap: 1,
        finishedAt: null,
      };
    });
    this.marbles = [...this.marbleByBodyId];
    this.events = replay.events;
  }

  start(): void {
    if (this.phase === 'ready') this.phase = 'racing';
  }

  step(deltaSeconds: number): void {
    if (this.phase !== 'racing' || !Number.isFinite(deltaSeconds) || deltaSeconds <= 0) return;
    this.elapsed = Math.min(this.replay.duration, this.elapsed + deltaSeconds);
    this.sampleReplay(this.elapsed);

    while (this.finishers.length < this.marbles.length) {
      const bodyId = this.replay.finishOrder[this.finishers.length];
      const marble = this.marbleByBodyId[bodyId];
      if (!marble || marble.targetFinishTime > this.elapsed + 1e-5) break;
      marble.finishedAt = marble.targetFinishTime;
      marble.progress = 1;
      this.finishers.push(marble);
    }
    if (this.finishers.length >= this.marbles.length) this.phase = 'complete';
  }

  private sampleReplay(elapsed: number): void {
    const frame = clamp(elapsed * this.replay.fps, 0, this.replay.frameCount - 1);
    const lowerFrame = Math.floor(frame);
    const upperFrame = Math.min(this.replay.frameCount - 1, lowerFrame + 1);
    const amount = frame - lowerFrame;
    const frameStride = this.replay.bodyCount * this.replay.valuesPerBody;
    for (const marble of this.marbleByBodyId) {
      const lower = lowerFrame * frameStride + marble.bodyId * this.replay.valuesPerBody;
      const upper = upperFrame * frameStride + marble.bodyId * this.replay.valuesPerBody;
      marble.x = this.replay.positions[lower] + (this.replay.positions[upper] - this.replay.positions[lower]) * amount;
      marble.y = this.replay.positions[lower + 1] + (this.replay.positions[upper + 1] - this.replay.positions[lower + 1]) * amount;
      marble.vx = this.replay.positions[lower + 2] + (this.replay.positions[upper + 2] - this.replay.positions[lower + 2]) * amount;
      marble.vy = this.replay.positions[lower + 3] + (this.replay.positions[upper + 3] - this.replay.positions[lower + 3]) * amount;
      marble.progress = this.replay.positions[lower + 4] + (this.replay.positions[upper + 4] - this.replay.positions[lower + 4]) * amount;
      if (this.replay.lapCount && this.replay.lapStartTimes) {
        const offset = marble.bodyId * this.replay.lapCount;
        marble.lap = 1;
        for (let lap = 1; lap < this.replay.lapCount; lap += 1) {
          if (elapsed + 1e-5 >= this.replay.lapStartTimes[offset + lap]) marble.lap = lap + 1;
        }
      }
    }
  }

  spinnerAngle(id: string, elapsed = this.elapsed): number | null {
    const spinnerIndex = this.spinnerIndexById.get(id);
    if (spinnerIndex === undefined) return null;
    const frame = clamp(elapsed * this.replay.fps, 0, this.replay.frameCount - 1);
    const lowerFrame = Math.floor(frame);
    const upperFrame = Math.min(this.replay.frameCount - 1, lowerFrame + 1);
    const amount = frame - lowerFrame;
    const spinnerCount = this.replay.spinnerIds.length;
    const lower = lowerFrame * spinnerCount + spinnerIndex;
    const upper = upperFrame * spinnerCount + spinnerIndex;
    return this.replay.spinnerAngles[lower]
      + (this.replay.spinnerAngles[upper] - this.replay.spinnerAngles[lower]) * amount;
  }

  get targetMarble(): RaceMarble {
    return this.marbles.find((marble) => marble.finishRank === this.targetRank)!;
  }

  get result(): RaceMarble | null {
    const arrived = this.map.revealOnTargetFinish && this.finishers.length >= this.targetRank;
    return this.phase === 'complete' || arrived ? this.targetMarble : null;
  }

  get remaining(): RaceMarble[] {
    return this.marbles.filter((marble) => marble.finishedAt === null);
  }

  currentOrder(): RaceMarble[] {
    return [...this.marbles].sort((left, right) => {
      if (left.finishedAt !== null && right.finishedAt !== null) return left.finishRank - right.finishRank;
      if (left.finishedAt !== null) return -1;
      if (right.finishedAt !== null) return 1;
      return right.progress - left.progress || left.serial - right.serial;
    });
  }

  eventPresentation(announcementLeadSeconds = 1.5): RaceEventPresentation | null {
    for (const event of this.events) {
      const announcesAt = Math.min(event.announcesAt, event.startsAt - Math.max(0, announcementLeadSeconds));
      if (this.elapsed >= announcesAt && this.elapsed < event.startsAt) {
        return { event, phase: 'announcing', countdown: event.startsAt - this.elapsed };
      }
      if (this.elapsed >= event.startsAt && this.elapsed <= event.startsAt + event.duration) {
        return { event, phase: 'active', countdown: 0 };
      }
    }
    return null;
  }

  collisionEffects(): CollisionEffect[] {
    const stride = this.replay.collisionValuesPerEvent;
    const active: CollisionEffect[] = [];
    for (let index = 0; index < this.replay.collisions.length; index += stride) {
      const startsAt = this.replay.collisions[index];
      const encodedKind = this.replay.collisions[index + 5];
      if (encodedKind !== 1) continue;
      const kind = 'bumper' as const;
      const duration = .38;
      if (startsAt > this.elapsed) break;
      if (this.elapsed > startsAt + duration) continue;
      active.push({
        id: `collision-${index / stride}`,
        x: this.replay.collisions[index + 1],
        y: this.replay.collisions[index + 2],
        dx: this.replay.collisions[index + 3],
        dy: this.replay.collisions[index + 4],
        kind,
        startsAt,
        duration,
        intensity: this.replay.collisions[index + 6],
      });
    }
    return active;
  }
}
